// ============================================================================
// IMPORTS
// ============================================================================
import { ColumnDef } from "@tanstack/react-table";
import { Link } from "@tanstack/react-router";
import { isPast } from 'date-fns';
import { Button } from './button';
import { MoreHorizontal, Edit, Trash2, ArrowUpDown, Users } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from './dropdown-menu';
import { getDocumentIcon } from '@/lib/document-icons';
import { createTableCheckboxColumn } from './column-builders';
import { ColumnHeaderFilter } from './data-table-filter-toolbar';
import {
  statusOptions as exportedStatusOptions,
  highlightText,
  customDateFilter,
} from './task-column-utils';
import { getTaskStateConfig, normalizeTaskState, type TaskState } from '@/components/tasks/constants';
import { useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { Command, CommandGroup, CommandItem, CommandList } from './command';
import { Input } from './input';
import { useMatterLocale } from '@/components/contexts/MatterJurisdictionContext';
import { formatRegionalDate } from '@/utils/date-utils';
import { SingleDateCalendar } from './DatePicker';
import { cn } from '@/lib/utils';
import { DeadlineTagChips } from './deadline-tag-chips';
import type { DeadlineTagVocabularyItem } from '@/hooks/useTagVocabulary';
import type { FileViewerSearch } from '@/src/router/search-schemas';
import type { MatterMember } from '@/types/base';


export function getSourceTooltip(task: Deadline): string | undefined {
  const sources = getTaskSourceDocuments(task);
  if (sources.length === 0) return undefined;
  const firstLabel = getSourceLabel(sources[0]);
  if (sources.length <= 1) return `Extracted from ${firstLabel}`;
  return `Extracted from ${firstLabel} and ${sources.length - 1} more source document${sources.length === 2 ? '' : 's'}`;
}

// ============================================================================
// HELPER COMPONENTS
// ============================================================================
export const PartyCell = ({
  task,
  uniqueParties,
  onUpdateParty,
  truncate = false,
}: {
  task: Deadline;
  uniqueParties: string[];
  onUpdateParty?: (taskId: string, party: string) => void;
  // When true, the party clips to a single line with an ellipsis instead of
  // wrapping (used where horizontal space is tight, e.g. the Table).
  truncate?: boolean;
}) => {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const party = task.party || 'Unknown';

  const filteredParties = uniqueParties.filter(p =>
    p.toLowerCase().includes(inputValue.toLowerCase())
  );

  const handleSelect = (selectedParty: string) => {
    if (onUpdateParty) {
      onUpdateParty(task.id, selectedParty);
    }
    setOpen(false);
    setInputValue('');
  };

  const handleAddNew = () => {
    if (inputValue && onUpdateParty) {
      onUpdateParty(task.id, inputValue);
    }
    setOpen(false);
    setInputValue('');
  };

  const showAddNew = inputValue.length > 0 &&
    !filteredParties.some(p => p.toLowerCase() === inputValue.toLowerCase());

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          title={truncate ? (party === 'Unknown' ? 'Set party' : party) : undefined}
          className={cn(
            'h-auto justify-start text-left font-normal hover:bg-gray-100',
            truncate
              ? 'inline-flex h-auto min-h-0 max-w-48 items-center gap-1 px-0 py-0 text-[11px] whitespace-nowrap'
              : 'min-h-8 w-full max-w-full min-w-0 px-2 py-1 text-sm whitespace-normal',
            party === 'Unknown' ? 'text-stone-400' : truncate ? 'text-stone-500' : 'text-gray-900',
          )}
        >
          {truncate ? (
            <>
              <Users className="h-3 w-3 shrink-0 opacity-70" />
              <span className="min-w-0 truncate">{party === 'Unknown' ? 'Set party' : party}</span>
            </>
          ) : (
            party
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[200px] p-0" align="start">
        <Command shouldFilter={false}>
          <div className="border-b px-3 py-2">
            <Input
              placeholder="Type to search or add..."
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && showAddNew) {
                  e.preventDefault();
                  handleAddNew();
                }
              }}
              className="h-8 border-0 p-0 focus-visible:ring-0 focus-visible:ring-offset-0"
              autoFocus
            />
          </div>
          <CommandList>
            <CommandGroup>
              {filteredParties.map(p => (
                <CommandItem
                  key={p}
                  onSelect={() => handleSelect(p)}
                  className="cursor-pointer"
                >
                  {p}
                </CommandItem>
              ))}
              {showAddNew && (
                <CommandItem
                  onSelect={handleAddNew}
                  className="cursor-pointer text-blue-600"
                >
                  <span className="mr-2">+</span>
                  Add "{inputValue}"
                </CommandItem>
              )}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

const TaskDateCell = ({
  task,
  onUpdateDate,
}: {
  task: Task;
  onUpdateDate?: (taskId: string, date: Date) => void;
}) => {
  const locale = useMatterLocale();
  const [open, setOpen] = useState(false);

  const date = task.date instanceof Date ? task.date : new Date(task.date);
  const isValid = !isNaN(date.getTime());
  const label = isValid ? formatRegionalDate(date, locale) : 'Invalid date';

  // Read-only rendering when no update handler is supplied.
  if (!onUpdateDate) {
    return (
      <div className={cn('text-sm whitespace-nowrap', isValid ? 'text-gray-900' : 'text-red-600')}>
        {label}
      </div>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={(e) => e.stopPropagation()}
          className={cn(
            'text-sm whitespace-nowrap rounded-sm px-2 py-1 text-left transition-colors hover:bg-gray-100',
            isValid ? 'text-gray-900' : 'text-red-600',
          )}
          title="Click to change due date"
        >
          {label}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start" onClick={(e) => e.stopPropagation()}>
        <SingleDateCalendar
          value={isValid ? date : undefined}
          onChange={(next) => {
            if (next) {
              onUpdateDate(task.id, next);
            }
            setOpen(false);
          }}
        />
      </PopoverContent>
    </Popover>
  );
};

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================
export type TaskSourceDocument = {
  document_id?: number;
  document_guid?: string | null;
  // The base event this source was extracted from (Event.guid). Used as the
  // citation focus id so a per-source click opens the right mention, even on a
  // multi-source synthetic deadline where task.event_guid is the synthetic id.
  canonical_event_guid?: string | null;
  document_name?: string | null;
  display_name?: string | null;
  subject?: string | null;
  is_attachment?: boolean;
  parent_document_guid?: string | null;
  folder_guid?: string | null;
  folder_name?: string | null;
  matched_text?: string | null;
  extracted_at?: string | null;
};

// Additive fields carried by synthetic (v2) deadlines. A synthetic deadline
// groups 1..n base deadlines and exposes candidate field values (each sourced,
// with a confidence). Date candidates additionally carry an EDTF expression
// plus derived range bounds. Populated in a later phase; usually empty today.
export type SyntheticDateFieldValue = {
  field_name: string;
  id?: string;
  value?: unknown;
  confidence?: number;
  reasoning?: string | null;
  // User-edited candidates outrank AI ones on the collapsed read path.
  is_user_edited?: boolean;
  // Stamped by the read path from the source event's `source`. Source authority
  // in the date winner policy: docket beats manual beats extracted.
  is_manual?: boolean;
  is_docket?: boolean;
  // When the candidate was recorded; the "latest evidence" tie-break in the
  // date winner policy.
  created_at?: string | null;
  // Present when field_name === 'date'.
  temporal_expression?: string | null;
  temporal_lower?: string | null;
  temporal_upper?: string | null;
  // Provenance for the detail-panel "Also mentioned" list: the base event
  // this candidate was recorded from, plus that event's own source documents
  // (not the row-level union, which de-dupes by document).
  source_event_id?: number | null;
  source_event_guid?: string | null;
  source_documents?: TaskSourceDocument[];
};

export type SyntheticTag = {
  id: string;
  label: string;
  origin: string;
};

export type SyncedCalendar = {
  calendar_id: string;
  original_calendar_id?: string;
  owner_address?: string;
  provider: string;
  external_event_id?: string;
  synced_at: string | null;
};

export type Deadline = {
  id: string;
  event_guid?: string;
  date: Date;
  description: string;
  notes?: string;
  state: TaskState;
  source_documents?: TaskSourceDocument[];
  created_by: 'auto' | 'manual' | number;
  created_by_name?: string;
  created_by_email?: string;
  assigned_to_guid?: string | null;
  assigned_to_name?: string;
  assigned_to_email?: string;
  party?: string | null;
  paragraph?: string;
  // Outlook calendar syncs for this deadline (list payload + ID-remapped on the page).
  synced_calendars?: SyncedCalendar[];
  // --- Additive synthetic (v2) deadline fields (optional; ignored by the
  // legacy Table view). See SyntheticDateFieldValue above. ---
  is_synthetic?: boolean;
  synthetic_deadline_id?: string | null;
  // Guids of the base events collapsed into a merged synthetic row. A merged
  // row's `id` is the synthetic id, so deep links that carry a base Event.guid
  // (assignment emails, in-app notifications) resolve to the row via this list.
  base_event_guids?: string[];
  title?: string | null;
  description_md?: string | null;
  // Multi-valued deadline classification (none/one/many). "other" is filtered
  // out server-side and never present here. ``is_procedural`` is a derived
  // rollup (true when any constituent type is procedural).
  deadline_types?: string[];
  is_procedural?: boolean;
  field_values?: SyntheticDateFieldValue[];
  tags?: SyntheticTag[];
  // Soft-delete state (synthetic deadlines only). Rows are only present when the
  // list was fetched with include_deleted=true (Table "Show deleted").
  is_deleted?: boolean;
  deleted_at?: string | null;
};

/** Compat alias while call sites migrate to Deadline. */
export type Task = Deadline;

/**
 * A "legacy" (old) deadline predates the synthetic-deadline system: it has no
 * synthetic link, and therefore no tags of its own. New deadlines are always
 * backed by a synthetic (single- or multi-link), so this cleanly separates the
 * two. Used to treat old deadlines as litigation for tag filtering.
 */
export function isLegacyDeadline(task: Deadline): boolean {
  return !task.synthetic_deadline_id && !task.is_synthetic;
}

export interface TaskActionHandlers {
  onConfirm: (taskId: string) => void;
  onEdit: (task: Deadline) => void;
  onComplete: (taskId: string) => void;
  onDelete: (taskId: string) => void;
  onMoveToInProgress: (taskId: string) => void;
  onMoveToNeedsConfirmation: (taskId: string) => void;
  onUpdateAssignee?: (taskId: string, assigneeGuid: string | null) => void;
  onUpdateDate?: (taskId: string, date: Date) => void;
  onUpdateParty?: (taskId: string, party: string) => void;
  onDocumentClick?: (documentGuid: string, taskId?: string) => void;
  /** Fired when a source link opens that document, with the table row id. */
  onSourceRowOpen?: (taskId: string, documentGuid: string) => void;
  // Restore a soft-deleted synthetic deadline. Only wired up by the Table
  // "Show deleted" view; left undefined elsewhere.
  onRestore?: (syntheticId: string) => void;
  // Add/remove a manual (user-created) tag on a synthetic deadline. Only the
  // synthetic-backed rows can be tagged; legacy base rows leave these undefined.
  onAddTag?: (syntheticId: string, label: string) => void;
  onRemoveTag?: (syntheticId: string, tagId: string) => void;
}

/** Value of an active date-range filter (matches customDateFilter's input). */
export interface DeadlineDateFilterValue {
  startDate?: Date | string;
  endDate?: Date | string;
}

/** Snapshot of the deadlines table's active column filters, shared by the page
 *  (auto-expand + highlight) and the column filterFns that populate it. */
export interface ActiveDeadlineFilters {
  description?: string[];
  state?: string[];
  date?: DeadlineDateFilterValue;
  assignee?: string[];
  tags?: string[];
  party?: string[];
}

export function getTaskSourceDocuments(task: Deadline): TaskSourceDocument[] {
  return (task.source_documents ?? []).filter((source): source is TaskSourceDocument => !!source?.document_guid);
}

function getSourceLabel(source?: TaskSourceDocument): string {
  return source?.display_name || source?.document_name || source?.subject || 'View Document';
}

// Turn a snake_case DeadlineType value into a human label, e.g.
// "pleading_filing" -> "Pleading Filing". "other" is filtered out server-side.
export function formatDeadlineType(value: string): string {
  return value
    .split('_')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

// Theme tags on a deadline, writing straight through to the API. Editable only
// for synthetic-backed rows (a legacy base row has no synthetic to tag).
export function DeadlineTagList({
  task,
  vocabulary,
  handlers,
  revealAddOnHover = true,
}: {
  task: Task;
  /** Controlled tag vocabulary, supplied by the page (not fetched per row). */
  vocabulary: DeadlineTagVocabularyItem[];
  handlers: TaskActionHandlers;
  /**
   * Defaults to the table's behaviour: the "+" stays hidden until the row is
   * hovered. Surfaces with room to spare (the detail panel) pass false so the
   * control is visible — and labelled — even when there are no tags yet.
   */
  revealAddOnHover?: boolean;
}) {
  const tags = Array.isArray(task.tags) ? task.tags : [];
  const syntheticId = task.synthetic_deadline_id ?? null;

  return (
    <DeadlineTagChips
      tags={tags}
      vocabulary={vocabulary}
      editable={!!syntheticId && !!handlers.onAddTag}
      revealAddOnHover={revealAddOnHover}
      className="mt-1"
      onAdd={(label) => {
        if (syntheticId) handlers.onAddTag?.(syntheticId, label);
      }}
      onRemove={(tag) => {
        if (syntheticId) handlers.onRemoveTag?.(syntheticId, tag.id);
      }}
    />
  );
}

function getSourceRouteFileId(source: TaskSourceDocument): string | null {
  if (source.is_attachment && source.parent_document_guid) {
    return source.parent_document_guid;
  }
  return source.document_guid ?? null;
}

function getSourceSearch(task: Task, source: TaskSourceDocument): FileViewerSearch {
  const includeTaskId = (source.is_attachment && !!source.parent_document_guid) || !!source.matched_text;
  return {
    from: 'case-files/correspondence',
    taskId: includeTaskId ? task.id : undefined,
  };
}

export function SourceDocumentLink({
  source,
  task,
  slug,
  handlers,
  showLabel = false,
  hideIcon = false,
  labelClassName,
}: {
  source: TaskSourceDocument;
  task: Task;
  slug: string;
  handlers: TaskActionHandlers;
  showLabel?: boolean;
  // Drop the file-type glyph (used by the toned-down Table Sources column).
  hideIcon?: boolean;
  // Override the label-variant container classes (text size/colour/hover).
  labelClassName?: string;
}) {
  const targetFileId = getSourceRouteFileId(source);
  const documentGuid = source.document_guid;
  if (!targetFileId || !documentGuid) return null;

  const { Icon, colorClass } = getDocumentIcon(source.document_name || '', false);
  const label = getSourceLabel(source);
  const linkSearch = getSourceSearch(task, source);

  return (
    <Link
      to="/matter/$slug/f/$fileId"
      params={{ slug, fileId: targetFileId }}
      search={linkSearch}
      onClick={(e) => {
        if (handlers.onDocumentClick && e.button === 0 && !e.ctrlKey && !e.metaKey && !e.shiftKey) {
          e.preventDefault();
          e.stopPropagation();
          handlers.onSourceRowOpen?.(task.id, documentGuid);
          handlers.onDocumentClick(documentGuid, source.canonical_event_guid ?? task.event_guid ?? task.id);
        }
      }}
      target={handlers.onDocumentClick ? undefined : "_blank"}
      rel={handlers.onDocumentClick ? undefined : "noopener noreferrer"}
      title={`Extracted from ${label}`}
      className={showLabel
        ? (labelClassName ?? "flex min-w-0 items-center gap-2 rounded px-2 py-1.5 text-left text-xs text-gray-700 hover:bg-gray-50 hover:text-gray-900")
        : "inline-flex h-5 w-5 items-center justify-center"}
    >
      {!hideIcon && <Icon className={`h-5 w-5 shrink-0 ${colorClass}`} />}
      {showLabel && <span className="min-w-0 truncate">{label}</span>}
    </Link>
  );
}

// ============================================================================
// EXPORTS (for backward compatibility)
// ============================================================================
export const statusOptions = exportedStatusOptions;
export { highlightText, customDateFilter };

// ============================================================================
// COLUMN DEFINITIONS
// ============================================================================
/**
 * Generates task table column definitions with integrated handlers
 *
 * @param handlers - Action handlers for task operations
 * @param allTasks - Complete list of tasks (for subtask lookups)
 * @param activeFiltersRef - Reference to active filters (for search highlighting)
 * @param slug - Matter slug for routing
 * @param hideFilters - Whether to hide filter controls
 * @param members - Team members for assignee dropdown
 */
export const getTaskColumns = (
  handlers: TaskActionHandlers,
  allTasks: Task[],
  activeFiltersRef?: React.MutableRefObject<ActiveDeadlineFilters>,
  slug?: string,
  hideFilters?: boolean,
  members?: MatterMember[],
  // Labels of the litigation-theme tags (from the controlled vocabulary). Old
  // deadlines are treated as litigation, so they surface whenever the active
  // tags filter includes any of these. Empty until the vocabulary loads.
  litigationTagLabels: string[] = [],
  // Full controlled tag vocabulary, supplied by the page so per-row tag chips
  // don't each instantiate useTagVocabulary. Empty until the vocabulary loads.
  tagVocabulary: DeadlineTagVocabularyItem[] = []
): ColumnDef<Task>[] => [
  // ============================================================================
  // CHECKBOX COLUMN - Row selection for bulk actions
  // ============================================================================
  createTableCheckboxColumn<Task>(),
  // ============================================================================
  // SOURCE COLUMN - Shows document icon for extracted tasks with link to source
  // Includes expand chevron for rows with subtasks
  // ============================================================================
  {
    id: 'source',
    meta: { filter: { type: 'multi', label: 'File type', order: 6 } },
    accessorFn: (row) => {
      const task = row;
      const sourceDocs = getTaskSourceDocuments(task);

      if ((task.created_by === null || task.created_by === undefined || task.created_by === 'auto') && sourceDocs.length > 0) {
        return Array.from(new Set(
          sourceDocs.map(sourceDoc => {
            const { fileType } = getDocumentIcon(sourceDoc.document_name || '', false);
            return fileType;
          })
        )); // Return as array for faceted filter
      }
      return [];
    },
    header: () => <div />,
    size: 40,
    minSize: 40,
    maxSize: 40,
    cell: ({ row }) => {
      const task = row.original;
      const sourceDocs = getTaskSourceDocuments(task);
      const sourceDoc = sourceDocs[0];

      // Show document icon for tasks extracted from documents (auto-extracted tasks have no created_by)
      if ((task.created_by === null || task.created_by === undefined || task.created_by === 'auto') && sourceDoc?.document_guid && slug) {
        const hasMultipleSources = sourceDocs.length > 1;
        const { Icon: SourceIcon, colorClass: sourceColorClass } = getDocumentIcon(sourceDoc.document_name || '', false);

        return (
          <div className="flex justify-center">
            <div className="relative flex h-5 w-5 items-center justify-center">
              {hasMultipleSources ? (
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      onClick={(e) => e.stopPropagation()}
                      className="relative inline-flex h-5 w-5 items-center justify-center"
                      title={getSourceTooltip(task)}
                      aria-label={getSourceTooltip(task)}
                    >
                      <SourceIcon className={`h-5 w-5 shrink-0 ${sourceColorClass}`} />
                      <span className="absolute -bottom-0.5 -right-1.5 z-10 flex h-3 min-w-3 items-center justify-center rounded-full border border-white bg-gray-100 px-0.5 text-[8px] font-semibold leading-none text-gray-700 shadow-sm">
                        +{sourceDocs.length - 1}
                      </span>
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-72 p-2" align="start">
                    <div className="px-2 pb-1 text-xs font-medium text-gray-500">
                      Source documents
                    </div>
                    <div className="max-h-64 overflow-y-auto">
                      {sourceDocs.map((source) => (
                        <SourceDocumentLink
                          key={source.document_guid}
                          source={source}
                          task={task}
                          slug={slug}
                          handlers={handlers}
                          showLabel
                        />
                      ))}
                    </div>
                  </PopoverContent>
                </Popover>
              ) : (
                <SourceDocumentLink
                  source={sourceDoc}
                  task={task}
                  slug={slug}
                  handlers={handlers}
                />
              )}
            </div>
          </div>
        );
      }


      return <div />;
    },
    filterFn: 'arrIncludesSome',
    enableSorting: false,
    enableHiding: false,
  },
  // ============================================================================
  // DESCRIPTION COLUMN - Task description with search highlighting
  // Filters both parent tasks and subtasks
  // ============================================================================
  {
    accessorKey: 'description',
    meta: { filter: { type: 'search', label: 'Description', order: 5 } },
    size: 400,
    minSize: 200,
    header: ({ table }) => {
      return (
        <div className="flex items-center gap-2 min-w-[100px]">
          <ColumnHeaderFilter table={table} columnId="description" className="mr-2">
            Description
          </ColumnHeaderFilter>
        </div>
      )
    },
    cell: ({ row, table }) => {
      const task = row.original;

      // Tags and the deadline-type pills stay behind the scenes in the table;
      // only reveal these inline chips once the user is explicitly filtering by
      // tags. (The row detail side panel renders them regardless.)
      const tagsFilterValue = table.getColumn('tags')?.getFilterValue();
      const tagsFilterActive = Array.isArray(tagsFilterValue) && tagsFilterValue.length > 0;

      const descriptionFilters = activeFiltersRef?.current?.description || [];
      const searchTerms = descriptionFilters.filter(f => f && f.trim() !== '');

      const sourceDocs = getTaskSourceDocuments(task);
      const sourceDoc = sourceDocs[0];
      const isExtracted = (task.created_by === null || task.created_by === undefined || task.created_by === 'auto');

      const isFileLink = !!slug && isExtracted && !!sourceDoc?.document_guid;
      const targetFileId = sourceDoc ? getSourceRouteFileId(sourceDoc) : null;
      const fileSearch: FileViewerSearch = sourceDoc
        ? getSourceSearch(task, sourceDoc)
        : { from: 'case-files/correspondence' };

      // One line so the row height stays put when the document panel narrows the table.
      const linkClassName = "block truncate text-left text-gray-900 hover:text-blue-600 transition-colors";
      const linkOnClick = (e: React.MouseEvent) => {
        if (handlers.onDocumentClick && isExtracted && sourceDoc?.document_guid && e.button === 0 && !e.ctrlKey && !e.metaKey && !e.shiftKey) {
          e.preventDefault();
          e.stopPropagation();
          handlers.onSourceRowOpen?.(task.id, sourceDoc.document_guid);
          handlers.onDocumentClick(sourceDoc.document_guid, sourceDoc.canonical_event_guid ?? task.event_guid ?? task.id);
        }
      };
      const target = handlers.onDocumentClick ? undefined : "_blank";
      const rel = handlers.onDocumentClick ? undefined : "noopener noreferrer";

      const title = getSourceTooltip(task);

      return (
        <div className="group/desccell flex items-start gap-2">
          <div className="flex-1 min-w-0">
            {isFileLink && targetFileId ? (
              <Link
                to="/matter/$slug/f/$fileId"
                params={{ slug, fileId: targetFileId }}
                search={fileSearch}
                onClick={linkOnClick}
                target={target}
                rel={rel}
                className={linkClassName}
                title={title}
              >
                {highlightText(task.description, searchTerms)}
              </Link>
            ) : (
              <div
                className="block truncate text-left text-gray-900"
                title={title}
              >
                {highlightText(task.description, searchTerms)}
              </div>
            )}
            {tagsFilterActive && Array.isArray(task.deadline_types) && task.deadline_types.length > 0 && (
              <div className="flex flex-wrap items-center gap-1 mt-1">
                {task.is_procedural && (
                  <span className="inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700 opacity-80">
                    Procedural
                  </span>
                )}
                {task.deadline_types.map((deadlineType) => (
                  <span
                    key={deadlineType}
                    className="inline-flex items-center rounded-full bg-slate-50 px-2 py-0.5 text-[11px] font-medium text-slate-500 opacity-80"
                  >
                    {formatDeadlineType(deadlineType)}
                  </span>
                ))}
              </div>
            )}
            {tagsFilterActive && (
              <DeadlineTagList task={task} vocabulary={tagVocabulary} handlers={handlers} />
            )}
          </div>
        </div>
      );
    },
    filterFn: (row, columnId, filterValue) => {
      // Store the current filter value in the ref so renderSubComponent can access it
      if (activeFiltersRef) {
        activeFiltersRef.current.description = filterValue || [];
      }

      // If no filter value, show the row
      if (!Array.isArray(filterValue) || filterValue.length === 0) return true;

      // Filter out empty strings
      const nonEmptyFilters = filterValue.filter(f => f && f.trim() !== '');
      if (nonEmptyFilters.length === 0) return true;

      // Check if the deadline itself matches
      const rowValue = row.getValue(columnId);
      const deadlineMatches = typeof rowValue === 'string' && nonEmptyFilters.some((filter) =>
        rowValue.toLowerCase().includes(filter.toLowerCase().trim())
      );

      return deadlineMatches;
    },
    enableSorting: false,
    enableHiding: false,
  },
  // ============================================================================
  // PARTY COLUMN - Responsible party with inline editing dropdown
  // ============================================================================
  {
    id: 'party',
    meta: { filter: { type: 'multi', label: 'Party', order: 3 } },
    accessorFn: (row) => row.party || 'Unknown',
    size: 120,
    minSize: 120,
    maxSize: 120,
    header: ({ table }) => {
      return (
        <div className="flex items-center gap-2 pl-2">
          <ColumnHeaderFilter table={table} columnId="party">Party</ColumnHeaderFilter>
        </div>
      );
    },
    cell: ({ row, table }) => {
      const task = row.original;
      const party = task.party || 'Unknown';

      // Get unique parties from all tasks for dropdown options
      const uniqueParties = Array.from(
        new Set(
          table.getPreFilteredRowModel().rows
            .map(r => r.original.party)
            .filter((p): p is string => !!p && p !== 'Unknown')
        )
      ).sort();

      return <PartyCell task={task} uniqueParties={uniqueParties} onUpdateParty={handlers.onUpdateParty} />;
    },
    filterFn: (row, id, value) => {
      if (!Array.isArray(value) || value.length === 0) return true;
      const party = row.original.party || 'Unknown';
      return value.includes(party);
    },
    enableSorting: true,
    enableHiding: false,
  },
  // ============================================================================
  // DATE COLUMN - Due date with relative time display
  // ============================================================================
  {
    accessorKey: 'date',
    meta: { filter: { type: 'date', label: 'Due Date', order: 4 } },
    size: 180,
    minSize: 180,
    maxSize: 180,
    header: ({ column, table }) => {
      return (
        <div className='flex flex-row items-center gap-0 pr-4'>
          <ColumnHeaderFilter table={table} columnId="date" className="whitespace-nowrap">
            Due date
          </ColumnHeaderFilter>
          <Button variant="ghost" size="sm" className="ml-2 w-6 h-8"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
            <ArrowUpDown className="h-4 w-4" strokeWidth={1.5} />
          </Button>
        </div>
      )
    },
    cell: ({ row }) => {
      return <TaskDateCell task={row.original} onUpdateDate={handlers.onUpdateDate} />;
    },
    filterFn: (row, columnId, filterValue) => {
      // Store the current filter value
      if (activeFiltersRef) {
        activeFiltersRef.current.date = filterValue;
      }
      return customDateFilter(row, columnId, filterValue);
    },
    sortingFn: (rowA, rowB) => {
      try {
        const dateA = rowA.original.date instanceof Date
          ? rowA.original.date.getTime()
          : new Date(rowA.original.date).getTime();
        const dateB = rowB.original.date instanceof Date
          ? rowB.original.date.getTime()
          : new Date(rowB.original.date).getTime();

        // Check for invalid dates
        if (isNaN(dateA) || isNaN(dateB)) {
          console.error('Invalid date in sorting:', rowA.original.date, rowB.original.date);
          return 0;
        }

        return dateA - dateB;
      } catch (error) {
        console.error('Error in date sorting:', error);
        return 0;
      }
    },
    enableSorting: true,
  },
  // ============================================================================
  // STATUS COLUMN - Task state with dropdown for state transitions
  // Includes overdue badge logic
  // ============================================================================
  {
    accessorKey: 'state',
    meta: { filter: { type: 'multi', label: 'Status', options: exportedStatusOptions, order: 1 } },
    size: 140,
    minSize: 140,
    maxSize: 140,
    header: ({ table }) => {
      return (
        <div className="flex items-center gap-2 pr-4">
          <ColumnHeaderFilter table={table} columnId="state">Status</ColumnHeaderFilter>
        </div>
      );
    },
    cell: ({ row }) => {
      const task = row.original;
      const state = normalizeTaskState(task.state);
      const config = getTaskStateConfig(state);
      const isOverdue = isPast(task.date) && state !== 'Completed';
      const isExtracted = !task.created_by;
      // Available state transitions based on current state and task type
      const getAvailableStates = () => {
        const states: Array<{ label: string; value: TaskState; action: () => void }> = [];

        // Can move to Needs Confirmation only if auto-extracted (not manually created)
        // This applies to both deadlines and subtasks
        if (state !== 'Extracted' && isExtracted) {
          states.push({ label: 'Move to Needs Confirmation', value: 'Extracted', action: () => handlers.onMoveToNeedsConfirmation(task.id) });
        }

        // Can move to Confirmed/In Progress from any state except if already Confirmed
        if (state !== 'Confirmed') {
          const label = 'Move to Confirmed';
          states.push({ label, value: 'Confirmed', action: () => handlers.onConfirm(task.id) });
        }

        // Can mark as completed from any state except if already Completed
        if (state !== 'Completed') {
          states.push({ label: 'Mark as Completed', value: 'Completed', action: () => handlers.onComplete(task.id) });
        }

        return states;
      };

      const availableStates = getAvailableStates();

      // "Confirmed" is a passive status, so hide it whenever a competing status
      // (e.g. Overdue) is present.
      const hideConfirmedForCompeting = state === 'Confirmed' && isOverdue;

      return (
        <div className="flex flex-col items-start gap-1">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              {hideConfirmedForCompeting ? (
                <button className="bg-red-100 text-red-700 border-0 whitespace-nowrap inline-flex items-center rounded-md px-2 py-1 text-xs font-medium cursor-pointer opacity-60 hover:opacity-100 transition-opacity">
                  Overdue
                </button>
              ) : (
                <button className={`${config.bg} ${config.text} border-0 whitespace-nowrap inline-flex items-center rounded-md px-2 py-1 text-xs font-medium cursor-pointer opacity-60 hover:opacity-100 transition-opacity`}>
                  {config.label}
                </button>
              )}
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              {availableStates.map((stateOption) => (
                <DropdownMenuItem key={stateOption.value} onClick={stateOption.action}>
                  {stateOption.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          {isOverdue && !hideConfirmedForCompeting && (
            <button className="bg-red-100 text-red-700 border-0 whitespace-nowrap inline-flex items-center rounded-md px-2 py-1 text-xs font-medium opacity-60 pointer-events-none">
              Overdue
            </button>
          )}
        </div>
      );
    },
    filterFn: (row, id, value) => {
      // Store the current filter value
      if (activeFiltersRef) {
        activeFiltersRef.current.state = value || [];
      }

      if (!Array.isArray(value) || value.length === 0) return true;

      const task = row.original;
      const taskState = row.getValue(id) as string;
      const isOverdue = isPast(task.date) && task.state !== 'Completed';

      // Check if any selected filter matches
      return value.some(filterValue => {
        if (filterValue === 'Overdue') {
          return isOverdue;
        }
        return filterValue === taskState;
      });
    },
    enableSorting: false,
  },
  // ============================================================================
  // ASSIGNEE COLUMN - Team member assignment with inline editing
  // ============================================================================
  {
    accessorKey: 'assigned_to_name',
    id: 'assignee',
    meta: { filter: { type: 'multi', label: 'Assignee', order: 2 } },
    size: 150,
    minSize: 150,
    maxSize: 150,
    header: ({ table }) => {
      return (
        <div className="flex items-center gap-2 pl-2 pr-4">
          <ColumnHeaderFilter table={table} columnId="assignee">Assignee</ColumnHeaderFilter>
        </div>
      );
    },
    cell: ({ row }) => {
      const task = row.original;
      const hasUpdateHandler = handlers.onUpdateAssignee;

      if (!hasUpdateHandler || !members || members.length === 0) {
        // No update handler or members - show static text
        return (
          <div className="text-sm">
            {task.assigned_to_name || (
              <span className="text-gray-400">Unassigned</span>
            )}
          </div>
        );
      }

      // Show dropdown for updating assignee
      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="text-sm hover:bg-gray-100 px-2 py-1 rounded-sm transition-colors text-left">
              {task.assigned_to_name || (
                <span className="text-gray-400">Unassigned</span>
              )}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            <DropdownMenuItem onClick={() => handlers.onUpdateAssignee!(task.id, null)}>
              <span className="text-gray-400 italic">Unassigned</span>
            </DropdownMenuItem>
            {members.map((member) => (
              <DropdownMenuItem
                key={member.user_guid}
                onClick={() => handlers.onUpdateAssignee!(task.id, member.user_guid)}
              >
                {member.name}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      );
    },
    filterFn: (row, id, value) => {
      // Store the current filter value
      if (activeFiltersRef) {
        activeFiltersRef.current.assignee = value || [];
      }

      if (!Array.isArray(value) || value.length === 0) return true;

      const task = row.original;

      // Check if parent task matches
      const parentMatches = value.some(filterValue => {
        if (filterValue === 'unassigned') {
          return !task.assigned_to_guid;
        }
        return task.assigned_to_guid === filterValue;
      });

      return parentMatches;
    },
    enableSorting: true,
  },
  // ============================================================================
  // TAGS COLUMN - Filter-only (hidden by default). Deadline theme tags stay
  // behind the scenes in the table and only surface inline in the description
  // cell while this filter is active; the row detail side panel always shows
  // them. Backs the "Tags" entry in the Filters menu.
  // ============================================================================
  {
    id: 'tags',
    accessorFn: (row) => (Array.isArray(row.tags) ? row.tags.map((tag) => tag.label) : []),
    meta: { filter: { type: 'multi', label: 'Tags', order: 7 } },
    header: () => null,
    enableSorting: false,
    filterFn: (row, id, value) => {
      if (!Array.isArray(value) || value.length === 0) return true;
      const labels = row.getValue(id) as string[] | undefined;
      if (Array.isArray(labels) && value.some((v) => labels.includes(v))) return true;
      // Old deadlines predate the synthetic-tagging system and carry no tags of
      // their own. Treat them as litigation: surface them whenever the active
      // filter includes any litigation-theme tag (e.g. clicking the "Litigation"
      // group heading selects them all), as if they had a "Litigation" tag.
      if (isLegacyDeadline(row.original) && (!labels || labels.length === 0)) {
        return value.some((v) => litigationTagLabels.includes(v));
      }
      return false;
    },
  },
  // ============================================================================
  // ACTIONS COLUMN - Dropdown menu with task operations
  // ============================================================================
  {
    id: 'actions',
    size: 50,
    minSize: 50,
    maxSize: 50,
    header: '',
    cell: ({ row }) => {
      const task = row.original;

      return (
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-8 w-8 p-0">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => handlers.onEdit(task)}>
              <Edit className="mr-2 h-4 w-4" />
              Edit
            </DropdownMenuItem>

            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => handlers.onDelete(task.id)}
              className="text-red-600 focus:text-red-600"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      );
    },
  },
];
